import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Paper,
  CircularProgress,
  Alert,
  Chip,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  TextField,
  Grid,
  Tooltip,
} from '@mui/material';
import { Download, Savings, Percent } from '@mui/icons-material';
import { discountOptimizerAPI, lookupAPI } from '../services/api';
import { Store, DiscountOptimizerItem, DiscountOptimizerMetadata } from '../types';

interface Props {
  selectedStore: Store | null;
}

const inr = (n: string | number | null | undefined) =>
  n == null || n === ''
    ? '—'
    : `₹${Number(n).toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

const pct = (n: string | number | null | undefined) =>
  n == null || n === '' ? '—' : `${Number(n)}%`;

const num = (v: string) => (v === '' || Number.isNaN(Number(v)) ? undefined : Number(v));

type SortKey = keyof Pick<
  DiscountOptimizerItem,
  'medicine_name' | 'distinct_customers' | 'bills' | 'revenue' | 'current_discount_pct' | 'current_margin_pct' | 'recommended_max_disc_pct' | 'est_annual_gain'
>;

const DiscountOptimizer: React.FC<Props> = ({ selectedStore }) => {
  const [stores, setStores] = useState<Store[]>([]);

  useEffect(() => {
    lookupAPI.getStores().then(setStores).catch(() => {});
  }, []);

  const [storeId, setStoreId] = useState<number | ''>(selectedStore ? selectedStore.id : '');
  const [lookbackDays, setLookbackDays] = useState('90');
  // Knobs — validated defaults. Cut depth is graduated by customer breadth.
  const [protectCustomers, setProtectCustomers] = useState('10');
  const [healthyMargin, setHealthyMargin] = useState('12');
  const [bigBillCap, setBigBillCap] = useState('1');
  const [bandNear, setBandNear] = useState('17');   // 6..(protect-1) customers
  const [bandMid, setBandMid] = useState('15');      // 4-5 customers
  const [bandNiche, setBandNiche] = useState('12');  // 1-3 customers (niche)

  const [rows, setRows] = useState<DiscountOptimizerItem[]>([]);
  const [meta, setMeta] = useState<DiscountOptimizerMetadata | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [sortKey, setSortKey] = useState<SortKey>('est_annual_gain');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');

  // Plain function (not memoized): the onClick/onChange handlers are recreated each
  // render, so it always closes over the current knob values. Auto-load is keyed on
  // storeId only (below), so tuning a knob doesn't fire a query until Recalculate.
  const loadReport = async () => {
    if (!storeId) return;
    setLoading(true);
    setError('');
    try {
      const res = await discountOptimizerAPI.getReport({
        store_id: storeId,
        lookback_days: num(lookbackDays),
        protect_customers: num(protectCustomers),
        healthy_margin_pct: num(healthyMargin),
        big_bill_cap_pct: num(bigBillCap),
        band_near_disc: num(bandNear),
        band_mid_disc: num(bandMid),
        band_niche_disc: num(bandNiche),
      });
      setRows(res.data);
      setMeta(res.metadata);
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.response?.data?.error || 'Failed to load discount optimizer');
      setRows([]);
      setMeta(null);
    } finally {
      setLoading(false);
    }
  };

  // Auto-load once a store is resolved; knob changes reload via the Recalculate button.
  useEffect(() => { if (storeId) loadReport(); }, [storeId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir(d => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('desc');
    }
  };

  const sortedRows = [...rows].sort((a, b) => {
    const av = a[sortKey];
    const bv = b[sortKey];
    const an = typeof av === 'string' && Number.isNaN(Number(av)) ? av : Number(av);
    const bn = typeof bv === 'string' && Number.isNaN(Number(bv)) ? bv : Number(bv);
    if (an < bn) return sortDir === 'asc' ? -1 : 1;
    if (an > bn) return sortDir === 'asc' ? 1 : -1;
    return 0;
  });

  const exportCSV = () => {
    const headers = [
      'Medicine', 'Manufacturer', 'Pack Size', 'Customers', 'Bills', 'Qty Sold', 'Revenue',
      'Current Disc %', 'Current Margin %', 'Recommended Max Disc %', 'Est Annual Gain',
    ];
    const csvRows = sortedRows.map(r => [
      r.medicine_name, r.manufacturer_name, r.pack_size || '',
      r.distinct_customers, r.bills, r.qty_sold, r.revenue,
      r.current_discount_pct, r.current_margin_pct,
      Number(r.recommended_max_disc_pct),
      r.reduced ? r.est_annual_gain : 0,
    ]);
    const csv = [headers, ...csvRows].map(row => row.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `discount_optimizer_store${storeId}_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const lift = meta?.projected_margin_lift_pp != null ? Number(meta.projected_margin_lift_pp) : null;
  const liftColor = lift == null ? '#9e9e9e' : lift >= 2 ? '#2e7d32' : lift >= 1.5 ? '#f57c00' : '#c62828';

  const knobField = (label: string, value: string, set: (v: string) => void, help: string, width = 130) => (
    <Tooltip title={help}>
      <TextField
        label={label}
        value={value}
        onChange={e => set(e.target.value)}
        size="small"
        type="number"
        sx={{ width }}
      />
    </Tooltip>
  );

  const tile = (label: string, value: string, color?: string, sub?: string) => (
    <Paper variant="outlined" sx={{ p: 1.5, textAlign: 'center', height: '100%' }}>
      <Typography variant="h5" sx={{ fontWeight: 700, color: color || 'text.primary' }}>{value}</Typography>
      <Typography variant="caption" color="textSecondary">{label}</Typography>
      {sub && <Typography variant="caption" color="textSecondary" sx={{ display: 'block' }}>{sub}</Typography>}
    </Paper>
  );

  return (
    <Box sx={{ p: 2 }}>
      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', mb: 1.5 }}>
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>Store</InputLabel>
            <Select value={storeId} label="Store" onChange={e => setStoreId(e.target.value as number)}>
              {stores.map(s => <MenuItem key={s.id} value={s.id}>{s.store_name}</MenuItem>)}
            </Select>
          </FormControl>
          {knobField('Lookback (days)', lookbackDays, setLookbackDays, 'Sales window analysed', 130)}
          <Button variant="contained" onClick={loadReport} disabled={!storeId || loading} startIcon={<Percent />}>
            Recalculate
          </Button>
          <Button size="small" startIcon={<Download />} onClick={exportCSV} disabled={rows.length === 0} sx={{ ml: 'auto' }}>
            CSV
          </Button>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          {knobField('Protect ≥ N customers', protectCustomers, setProtectCustomers, 'Items bought by at least this many distinct customers keep full 20% (broad-base / traffic products).', 165)}
          {knobField('Healthy margin ≥ %', healthyMargin, setHealthyMargin, 'Items already at or above this gross margin are left untouched.', 150)}
          {knobField('Big-bill cap %', bigBillCap, setBigBillCap, 'No bill above ₹1000 may rise more than this percent.', 130)}
          {knobField('Disc: 6-9 customers', bandNear, setBandNear, 'Max Disc % for items with a moderate customer base (6 up to the protect cutoff) — a small, barely-noticeable cut.', 155)}
          {knobField('Disc: 4-5 customers', bandMid, setBandMid, 'Max Disc % for items bought by 4-5 customers.', 150)}
          {knobField('Disc: 1-3 customers', bandNiche, setBandNiche, 'Max Disc % for niche items bought by only 1-3 customers — the deepest cut, felt by almost no one.', 150)}
        </Box>
      </Paper>

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>
      )}

      {!loading && error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {!loading && !error && meta && (
        <>
          <Grid container spacing={1.5} sx={{ mb: 2 }}>
            <Grid item xs={6} md={3}>
              {tile('Projected margin lift', lift != null ? `+${lift.toFixed(2)} pp` : '—', liftColor, 'target ≥ 2.00 pp')}
            </Grid>
            <Grid item xs={6} md={3}>{tile('Est. annual gain', inr(meta.harvest_annual), '#2e7d32')}</Grid>
            <Grid item xs={6} md={3}>{tile('Items reduced', `${meta.items_affected} / ${meta.total_items}`, '#1565c0', 'of all items shown')}</Grid>
            <Grid item xs={6} md={3}>{tile('Big bills protected', String(meta.big_bills_capped ?? 0), '#6a1b9a', 'capped at 1%, no breach')}</Grid>
          </Grid>

          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            <strong>Every item that sold</strong> is listed with the <strong>Max Disc.(%)</strong> to set in evitalRX — items
            we're not cutting show <strong>20%</strong> so nothing is missed. Cut depth is graduated by how many customers buy
            the item: a moderate base (6-{meta.knobs.protect_customers - 1} customers) gets only a small {meta.knobs.band_near_disc}%
            cap; the deepest {meta.knobs.band_niche_disc}% applies only to niche items bought by 1-3 customers, so regular buyers
            barely notice. Items bought by ≥{meta.knobs.protect_customers} customers or already ≥{meta.knobs.healthy_margin_pct}%
            margin keep the full 20%.
            {meta.item_list_capped && ' (List capped at 10000 rows.)'}
          </Typography>

          {rows.length === 0 ? (
            <Alert severity="info">No sales found for this store and window.</Alert>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow sx={{ '& th': { bgcolor: '#f5f5f5', fontWeight: 600 } }}>
                    <TableCell sortDirection={sortKey === 'medicine_name' ? sortDir : false}>
                      <TableSortLabel active={sortKey === 'medicine_name'} direction={sortKey === 'medicine_name' ? sortDir : 'asc'} onClick={() => handleSort('medicine_name')}>Medicine</TableSortLabel>
                    </TableCell>
                    <TableCell>Manufacturer</TableCell>
                    <TableCell>Pack</TableCell>
                    <TableCell align="right" sortDirection={sortKey === 'distinct_customers' ? sortDir : false}>
                      <TableSortLabel active={sortKey === 'distinct_customers'} direction={sortKey === 'distinct_customers' ? sortDir : 'asc'} onClick={() => handleSort('distinct_customers')}>Customers</TableSortLabel>
                    </TableCell>
                    <TableCell align="right" sortDirection={sortKey === 'bills' ? sortDir : false}>
                      <TableSortLabel active={sortKey === 'bills'} direction={sortKey === 'bills' ? sortDir : 'asc'} onClick={() => handleSort('bills')}>Bills</TableSortLabel>
                    </TableCell>
                    <TableCell align="right" sortDirection={sortKey === 'revenue' ? sortDir : false}>
                      <TableSortLabel active={sortKey === 'revenue'} direction={sortKey === 'revenue' ? sortDir : 'asc'} onClick={() => handleSort('revenue')}>Revenue</TableSortLabel>
                    </TableCell>
                    <TableCell align="right" sortDirection={sortKey === 'current_discount_pct' ? sortDir : false}>
                      <TableSortLabel active={sortKey === 'current_discount_pct'} direction={sortKey === 'current_discount_pct' ? sortDir : 'asc'} onClick={() => handleSort('current_discount_pct')}>Current Disc</TableSortLabel>
                    </TableCell>
                    <TableCell align="right" sortDirection={sortKey === 'current_margin_pct' ? sortDir : false}>
                      <TableSortLabel active={sortKey === 'current_margin_pct'} direction={sortKey === 'current_margin_pct' ? sortDir : 'asc'} onClick={() => handleSort('current_margin_pct')}>Current Margin</TableSortLabel>
                    </TableCell>
                    <TableCell align="right" sortDirection={sortKey === 'recommended_max_disc_pct' ? sortDir : false}>
                      <TableSortLabel active={sortKey === 'recommended_max_disc_pct'} direction={sortKey === 'recommended_max_disc_pct' ? sortDir : 'asc'} onClick={() => handleSort('recommended_max_disc_pct')}>Recommended Max Disc</TableSortLabel>
                    </TableCell>
                    <TableCell align="right" sortDirection={sortKey === 'est_annual_gain' ? sortDir : false}>
                      <TableSortLabel active={sortKey === 'est_annual_gain'} direction={sortKey === 'est_annual_gain' ? sortDir : 'asc'} onClick={() => handleSort('est_annual_gain')}>Est. Annual Gain</TableSortLabel>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sortedRows.map(r => (
                    <TableRow key={`${r.medicine_name}-${r.pack_size ?? ''}`} hover sx={!r.reduced ? { bgcolor: '#fafafa' } : undefined}>
                      <TableCell>{r.medicine_name}</TableCell>
                      <TableCell>{r.manufacturer_name}</TableCell>
                      <TableCell>{r.pack_size || '—'}</TableCell>
                      <TableCell align="right">{r.distinct_customers}</TableCell>
                      <TableCell align="right">{r.bills}</TableCell>
                      <TableCell align="right">{inr(r.revenue)}</TableCell>
                      <TableCell align="right">{pct(r.current_discount_pct)}</TableCell>
                      <TableCell align="right">{pct(r.current_margin_pct)}</TableCell>
                      <TableCell align="right">
                        {r.reduced
                          ? <Chip size="small" color="primary" icon={<Percent />} label={`${Number(r.recommended_max_disc_pct)}%`} />
                          : <Chip size="small" variant="outlined" label="20%" />}
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600, color: '#2e7d32' }}>
                        {r.reduced ? inr(r.est_annual_gain) : '—'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </>
      )}
    </Box>
  );
};

export default DiscountOptimizer;
